import React, { HTMLAttributes, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { verifyOtp } from "../../../apis/otp";
import BaseButton from "../../../components/atom/baseButton";
import BaseCarplateTextInput from "../../../components/atom/baseCarplateTextInput";
import BaseDarkButton from "../../../components/atom/baseDarkButton";
import BaseTextInput from "../../../components/atom/baseTextInput";
import BaseTitle from "../../../components/atom/baseTitle";
import OtpRequestButton from "../../../components/atom/otpRequestButton";
import { _8digitValidator } from "../../../helper/checker";
import useReceiptStore from "../../../store/receipt";
import ReceiptParkingCard from "../parkingCard";

interface IReceiptInquireForm {
  goNext: () => void;
  goStep?: (step: number) => void;
  goHomeAndRemoveQueryParam: () => void;
}

export interface IInquireFormValues {
  mainCarPlate: string;
  phone: string;
  otp: string;
}

interface IInquireFormErrorMessage extends Partial<IInquireFormValues> {}

type TReceiptInquireFormProps = IReceiptInquireForm &
  HTMLAttributes<HTMLDivElement>;

const ReceiptInquireForm: React.FC<TReceiptInquireFormProps> = (props) => {
  const { goNext, goStep, goHomeAndRemoveQueryParam, ...otherProps } = props;
  const navigate = useNavigate();

  const [receiptInquireFormValues, setReceiptInquireFormValues] =
    useState<IInquireFormValues>({
      mainCarPlate: "",
      phone: "",
      otp: "",
    });

  const [errorMessages, setErrorMessages] = useState<IInquireFormErrorMessage>(
    {}
  );

  const { receiptFormData, setReceiptFormData } = useReceiptStore(
    (state) => state
  );

  //   const carparkInfoList = useReceiptStore((state) => state.receiptParkingList);

  const onchangereceiptInquireFormValues = (
    key: keyof IInquireFormValues,
    value: string
  ) => {
    setReceiptInquireFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const onsubmitReceiptInquireForm = async () => {
    // other form validation
    try {
      let tmpMsg = {};

      // check if main car plate is empty
      if (receiptInquireFormValues.mainCarPlate.trim().length === 0) {
        tmpMsg = { ...errorMessages, mainCarPlate: "請輸入車牌號碼" };
      } else {
        tmpMsg = { ...errorMessages, mainCarPlate: "" };
      }

      if (receiptInquireFormValues.phone.trim().length === 0) {
        tmpMsg = { ...errorMessages, phone: "請輸入電話號碼" };
      } else {
        tmpMsg = { ...errorMessages, phone: "" };
      }

      setErrorMessages((prev) => ({ ...prev, ...tmpMsg }));

      // check otp is empty
      if (receiptInquireFormValues.otp.length < 6) {
        setErrorMessages((prev) => ({
          ...prev,
          otp: "請輸入6位數字驗證碼",
        }));
        return;
      } else {
        setErrorMessages((prev) => ({ ...prev, otp: "" }));
      }

      //   if no error, go to next step
      const verityOtpResponse = await verifyOtp(
        receiptInquireFormValues.phone,
        receiptInquireFormValues.otp
      );

      if (verityOtpResponse.message === "success") {
        console.log("should go next page");
        setReceiptFormData(receiptInquireFormValues);
        goNext();
      } else {
        setErrorMessages((prev) => ({
          ...prev,
          otp: "驗證碼錯誤，請重新輸入",
        }));
        return;
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (receiptFormData) {
      setReceiptInquireFormValues(receiptFormData);
    }
  }, [receiptFormData]);

  return (
    <div className="flex flex-1 flex-col gap-4 py-4" {...otherProps}>
      <BaseTitle
        text="下載電子收據"
        subTitle="Download E-Receipt"
        textColor="text-white"
      />

      <div className="flex flex-col gap-3 text-white font-medium text-sm text-center mb-4">
        透過實現無紙張化，
        <br />與 ParkingBees ⼀起邁向可持續發展
        <div>
          Move towards sustainability with ParkingBees by going paperless.
        </div>
      </div>

      <ReceiptParkingCard />

      {/* input form section */}
      <div className="flex flex-col gap-4 my-3 justify-center items-center">
        <BaseCarplateTextInput
          placeholder="車牌號碼 License Plate"
          value={receiptInquireFormValues?.mainCarPlate}
          onChangeCarplate={(value) => {
            onchangereceiptInquireFormValues("mainCarPlate", value);
          }}
          errorMessage={errorMessages.mainCarPlate}
        />

        <BaseTextInput
          placeholder="電話號碼 Phone"
          required
          value={receiptInquireFormValues?.phone}
          rightElement={
            <OtpRequestButton phone={receiptInquireFormValues?.phone} />
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const isPhoneValid = _8digitValidator(e.target.value);
            if (!isPhoneValid) {
              return;
            }
            onchangereceiptInquireFormValues("phone", e.target.value);
          }}
          errorMessage={errorMessages?.phone}
        />

        <BaseTextInput
          placeholder="驗證碼 OTP"
          required
          value={receiptInquireFormValues.otp}
          maxLength={6}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const otpRegex = /^[0-9]{0,6}$/;

            if (!otpRegex.test(e.target.value)) {
              return;
            }
            onchangereceiptInquireFormValues("otp", e.target.value);
          }}
          errorMessage={errorMessages?.otp}
        />

        <BaseButton
          disabled={
            !receiptInquireFormValues.mainCarPlate ||
            !receiptInquireFormValues.phone ||
            !receiptInquireFormValues.otp
          }
          onClick={onsubmitReceiptInquireForm}
          minWidth="w-full"
          disabledStyle="disabled:bg-mainYellow disabled:bg-opacity-60"
        >
          下一步 Next
        </BaseButton>

        <BaseDarkButton onClick={goHomeAndRemoveQueryParam}>
          其他停車場 Other Parking
        </BaseDarkButton>
      </div>
    </div>
  );
};

export default ReceiptInquireForm;
